import {
    fromBase64Url,
    linkAccessToken,
    unwrap,
    deriveSubkeys,
} from '../crypto/activityCrypto.js';

/**
 * Accès d'un lecteur web à une session, chiffrée de bout en bout :
 *   lien `…/session/{id}#k=<secret>` ; le navigateur n'envoie jamais le
 *   fragment au serveur. On en dérive un jeton (en-tête X-Activity-Access) et
 *   on ouvre soi-même l'enveloppe de la clé. Le secret est retiré de la barre
 *   d'adresse dès sa lecture (historique, captures, copier-coller).
 */
let captured;
let tokenPromise;

/**
 * Le secret retiré de la barre d'adresse est gardé pour cet onglet seulement
 * (sessionStorage, effacé à sa fermeture) : un rechargement ou le passage à
 * la relecture ne doivent pas faire perdre l'accès.
 */
const sessionIdFromPath = () => (window.location.pathname.match(/\/session\/([^/]+)/) || [])[1] || null;
const storageKey = sessionId => `monalerte.session-access.${sessionId}`;

function rememberSecret(secret) {
    const sessionId = sessionIdFromPath();
    try { if (sessionId) window.sessionStorage.setItem(storageKey(sessionId), secret); } catch (_) { /* stockage indisponible */ }
}

function recallSecret() {
    const sessionId = sessionIdFromPath();
    try { return sessionId ? window.sessionStorage.getItem(storageKey(sessionId)) : null; } catch (_) { return null; }
}

export function captureSessionAccess() {
    if (captured !== undefined) return captured;
    const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const fromFragment = fragment.get('k');
    const secret = fromFragment || recallSecret();
    captured = null;
    if (secret) {
        try {
            const linkSecret = fromBase64Url(secret);
            if (linkSecret.length === 32) {
                captured = { version: 2, linkSecret };
                if (fromFragment) rememberSecret(fromFragment);
            }
        } catch (_) { /* lien abîmé */ }
        if (fromFragment) window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`);
    }
    return captured;
}

/**
 * Accès fourni par l'application hôte (dashboard, lien police) au lieu de
 * l'adresse de la page. null : revient à la lecture de l'adresse.
 */
export function configureSessionAccess(access) {
    captured = access === null ? undefined : access;
    tokenPromise = undefined;
}

export const isV2Access = () => captureSessionAccess()?.version === 2;

/** Aucun paramètre de requête : l'accès passe par l'en-tête dérivé du secret du lien. */
export function accessQuery() {
    return '';
}

/** En-têtes v2 (jeton dérivé du secret du lien) ; vide en v1. */
export async function accessHeaders() {
    const access = captureSessionAccess();
    // En-têtes ajoutés par l'hôte (dashboard : compte admin, pas concerné par la
    // fermeture du lien une heure après la fin de la session).
    const extra = access?.extraHeaders || {};
    if (access?.version !== 2) return { ...extra };
    // Lien police : jeton déjà dérivé du mot de passe par l'hôte.
    if (access.token) return { ...extra, 'X-Activity-Access': access.token };
    tokenPromise ??= linkAccessToken(access.linkSecret);
    return { ...extra, 'X-Activity-Access': await tokenPromise };
}

/** Ajoute l'accès v1 à une URL d'API (v2 : rien, l'accès passe en en-tête). */
export function withAccessQuery(url) {
    const query = accessQuery();
    if (!query) return url;
    return `${url}${url.includes('?') ? '&' : '?'}${query}`;
}

/**
 * v2 : récupère l'enveloppe du lien et l'ouvre. Renvoie les sous-clés
 * { events (16 o, rôle de pswdSession), mediaHls (16 o) } ; null si refusé.
 */
export async function openLinkKeys(apiBaseUrl, sessionId) {
    const access = captureSessionAccess();
    if (access?.version !== 2) return null;
    if (access.keys) return access.keys; // clés déjà ouvertes par l'hôte (lien police)
    const response = await fetch(`${apiBaseUrl.replace(/\/+$/, '')}/api/session/${encodeURIComponent(sessionId)}/wraps/link`, {
        headers: await accessHeaders(),
        cache: 'no-store',
    });
    if (!response.ok) return null;
    const { envelope } = await response.json();
    const activityKey = await unwrap(envelope, sessionId, { linkSecret: access.linkSecret });
    const { events, mediaHls } = await deriveSubkeys(activityKey);
    return { events, mediaHls };
}
